import useAppContext from 'flow/hooks/useAppContext';
import { useVoiceSelection } from 'flow/hooks/useVoiceSelection';
import { useTTSGeneration } from 'flow/hooks/useTTSGeneration';
import { useLanguageSelection } from 'flow/hooks/useLanguageSelection';
import { useState } from 'react';
import AudioPlayer from './AudioPlayer';
import CreateSoundModalConfirm from './modals/CreateSoundModalConfirm';
import TextArea from './ui-components/TextArea';
import Button from './ui-components/Button';
import LanguageVoiceSelector from './LanguageVoiceSelector';
import { twMerge } from 'tailwind-merge';

interface Props {
    setFile: (file: File | null) => void;
    className?: string;
}

const TextToSpeech = ({ setFile, className }: Props) => {
    const { i18next } = useAppContext();
    const [text, setText] = useState('');

    const { languageCode, setLanguageCode } = useLanguageSelection();
    const { voice, setVoice, availableVoices } =
        useVoiceSelection(languageCode);
    const {
        loading,
        audioResult,
        showConfirm,
        setShowConfirm,
        checkConfirm,
        generateTTS,
    } = useTTSGeneration(setFile);

    const handleLanguageChange = (newLanguageCode: string) => {
        setLanguageCode(newLanguageCode);
    };

    const handleGenerate = () => {
        generateTTS(text, languageCode, voice);
    };

    const handleConfirmGenerate = () => {
        checkConfirm(handleGenerate);
    };

    return (
        <div className={twMerge('flex flex-col gap-4', className)}>
            <TextArea
                id="title"
                rows={2}
                value={text}
                onChange={(e) => setText(e.target.value)}
                disabled={loading}
                placeholder={i18next.t('Recorder.tts.text.script')}
            />

            <LanguageVoiceSelector
                languageCode={languageCode}
                onLanguageChange={handleLanguageChange}
                voice={voice}
                onVoiceChange={setVoice}
                availableVoices={availableVoices}
                disabled={loading}
            />

            <Button
                color="primary"
                onClick={handleConfirmGenerate}
                disabled={loading || !text}
            >
                {i18next.t('Recorder.tts.generate.sound')}
            </Button>

            {audioResult && (
                <AudioPlayer
                    url={audioResult.url}
                    uniqueId={audioResult.name}
                    duration={audioResult.duration}
                    className="dark:!bg-gray-100"
                />
            )}

            {showConfirm && (
                <CreateSoundModalConfirm
                    onSubmit={() => {
                        handleGenerate();
                        setShowConfirm(false);
                    }}
                    onClose={() => setShowConfirm(false)}
                />
            )}
        </div>
    );
};

export default TextToSpeech;
